import React from 'react';
import { Box, Typography, Paper, Container } from '@mui/material';
import { ACCENT, SECONDARY, PRIMARY } from '../../theme/theme';

// Feature item component
interface FeatureItemProps {
  title: string;
  description: string;
  icon: string;
  color: string;
}

const FeatureItem: React.FC<FeatureItemProps> = ({ title, description, icon, color }) => (
  <Paper 
    elevation={0} 
    sx={{ 
      p: 3, 
      height: '100%',
      borderRadius: 2,
      border: '1px solid rgba(0, 0, 0, 0.08)',
      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
      '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: '0 10px 20px rgba(0, 0, 0, 0.1)'
      }
    }}
  >
    <Box 
      sx={{ 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 60,
        height: 60,
        borderRadius: '50%',
        backgroundColor: `${color}20`,
        mb: 2
      }}
    >
      <Typography 
        sx={{ 
          fontSize: '1.5rem', 
          color: color 
        }}
      >
        {icon}
      </Typography>
    </Box>
    <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    <Typography variant="body1" color="text.secondary">
      {description}
    </Typography>
  </Paper>
);

const Features: React.FC = () => {
  const features = [
    {
      title: 'Content Discovery',
      description: 'Browse through a personalized feed of curated microlearning lessons with advanced filtering options.',
      icon: '🔍',
      color: PRIMARY
    },
    {
      title: 'Multiple Content Types',
      description: 'Access various formats including text, images, quotes, book recommendations, and article links.',
      icon: '📚',
      color: SECONDARY
    },
    {
      title: 'Favorites System',
      description: 'Mark both entire lessons and individual entries as favorites for easy access later.',
      icon: '⭐',
      color: ACCENT
    }
  ];

  return (
    <Box 
      sx={{ 
        py: 8,
        backgroundColor: '#f9f9f9'
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h3" 
            component="h2" 
            gutterBottom
            sx={{ fontWeight: 700 }}
          >
            Key Features
          </Typography>
          <Typography 
            variant="h6" 
            color="text.secondary" 
            sx={{ 
              maxWidth: 700, 
              mx: 'auto',
              mb: 2 
            }}
          >
            Designed to make learning efficient, engaging, and accessible anywhere
          </Typography>
        </Box>
        
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 4 }}>
          {features.map((feature, index) => (
            <Box key={index}>
              <FeatureItem {...feature} />
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default Features;
